import { getSchoolYearDateRange } from './get-school-year';

// Format a local date back into `yyyy-MM-dd`.
function toDateParam(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Parse a strict `yyyy-MM-dd` string as a local calendar date, matching the
// report date-range logic (which works in local time). Returns null for a
// malformed string or a non-existent calendar date (e.g. `2026-02-31`). The
// canonical round-trip (parse, then re-format and compare) rejects anything
// that is not exactly zero-padded `yyyy-MM-dd` — no regex needed.
function parseDateParam(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    return null;
  }
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  return toDateParam(date) === value ? date : null;
}

function notFound(): never {
  // react-router surfaces a thrown Response through the route's errorElement.
  // eslint-disable-next-line @typescript-eslint/no-throw-literal
  throw new Response('Not Found', { status: 404 });
}

/**
 * Validate the optional `from`/`to` report date-range params carried on a
 * report URL.
 *
 * The params are valid only when BOTH are present exactly once, are real
 * `yyyy-MM-dd` dates, `from` is not after `to`, `from` is not earlier than the
 * most recent July 15 (the school-year start), and `to` is not later than
 * today. Anything else throws a 404 `Response`. Absent params (the default
 * "Current school year") are valid.
 */
export function validateDateRangeParams(request: Request): void {
  const { searchParams } = new URL(request.url);
  const froms = searchParams.getAll('from');
  const tos = searchParams.getAll('to');

  // No date params at all → default range, nothing to validate.
  if (froms.length === 0 && tos.length === 0) return;

  // Require exactly one of each: reject a missing half or duplicate/conflicting
  // params (e.g. `?from=a&from=b`, or `?from=a` with no `to`).
  const from = froms[0];
  const to = tos[0];
  if (
    froms.length !== 1 ||
    tos.length !== 1 ||
    from === undefined ||
    to === undefined
  ) {
    notFound();
  }

  const fromDate = parseDateParam(from);
  const toDate = parseDateParam(to);
  if (!fromDate || !toDate) notFound();

  const { from: schoolYearStart, to: today } = getSchoolYearDateRange();
  if (
    fromDate > toDate || // `from` after `to`
    fromDate < schoolYearStart || // `from` earlier than the most recent July 15
    toDate > today // `to` later than today
  ) {
    notFound();
  }
}
